import { useState, useEffect, useRef } from "react";
import { speak, findVoice, lookupWord } from "../../utils/words";
import { DictionaryPopup } from "../shared/DictionaryPopup";

export function SpellingTest({ words, onComplete, voicePrefs, sentenceMode, onSaveAndExit, onExit, initialIdx = 0, initialResults = [], initialStartTime }) {
  const [idx, setIdx] = useState(initialIdx);
  const [ans, setAns] = useState("");
  const [done, setDone] = useState(false);
  const [correct, setCorrect] = useState(null);
  const [results, setResults] = useState(initialResults);
  const [t0] = useState(initialStartTime || Date.now());
  const [shaking, setShaking] = useState(false);
  const [dictWord, setDictWord] = useState(null);
  const [sentenceLoading, setSentenceLoading] = useState(false);
  const [sentence, setSentence] = useState(null);
  const inputRef = useRef();
  const word = words[idx];
  const pct = Math.round((idx / words.length) * 100);

  useEffect(() => {
    setSentence(null);
    if (!done) { setTimeout(() => speak(word.word, voicePrefs), 300); setTimeout(() => inputRef.current?.focus(), 80); }
  }, [idx, done, word.word]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleHearSentence() {
    if (sentence) { speakSentence(sentence); return; }
    setSentenceLoading(true);
    const entry = await lookupWord(word.word);
    const ex = entry?.results?.find(r => r.examples?.length)?.examples?.[0]
            || entry?.results?.[0]?.definition;
    const text = ex || `${word.word} — no example found.`;
    setSentence(text);
    setSentenceLoading(false);
    speakSentence(text);
  }

  function speakSentence(text) {
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    const voice = findVoice(voicePrefs);
    if (voice) u.voice = voice;
    u.lang = voicePrefs?.accent || "en-AU";
    u.rate = 0.82;
    window.speechSynthesis.speak(u);
  }

  function handleSaveAndExit() {
    const saveIdx = done ? idx + 1 : idx;
    if (saveIdx >= words.length) { onComplete(results, Math.round((Date.now() - t0) / 1000)); return; }
    onSaveAndExit?.({ quizType: "spelling", currentIndex: saveIdx, results, words, startTime: t0, savedAt: Date.now(), expiresAt: new Date().setHours(23,59,59,999) });
  }

  function submit() {
    if (!ans.trim()) return;
    const ok = ans.trim().toLowerCase() === word.word.toLowerCase();
    setCorrect(ok); setDone(true);
    if (!ok) { setShaking(true); setTimeout(() => setShaking(false), 420); }
    setResults(r => [...r, { word, userAnswer: ans.trim(), correct: ok }]);
  }
  function next() {
    const res = results; setAns(""); setDone(false); setCorrect(null); setSentence(null);
    if (idx + 1 >= words.length) onComplete(res, Math.round((Date.now() - t0) / 1000));
    else setIdx(i => i + 1);
  }

  return (
    <div className="test-screen">
      {dictWord && <DictionaryPopup word={dictWord} onClose={() => setDictWord(null)} />}
      <div className="test-prog-bar"><div className="test-prog-fill" style={{ width: `${pct}%` }} /></div>
      <div className="test-body">
        <div className="test-ctr">Word {idx + 1} of {words.length}</div>
        <div className={`word-card ${shaking ? "shake" : ""}`}>
          <div style={{ display: "flex", gap: ".5rem", justifyContent: "center", flexWrap: "wrap", marginBottom: ".25rem" }}>
            <button className="speak-btn" onClick={() => speak(word.word, voicePrefs)}>🔊 Hear word</button>
            {sentenceMode && (
              <button className="speak-btn" style={{ background: "var(--purple)", opacity: sentenceLoading ? 0.65 : 1 }}
                onClick={handleHearSentence} disabled={sentenceLoading}>
                {sentenceLoading ? "⏳" : "💬"} {sentence ? "Again" : "In a sentence"}
              </button>
            )}
          </div>
          {sentence && (
            <div style={{ fontSize: ".8rem", color: "var(--muted)", fontStyle: "italic", textAlign: "center", margin: ".4rem .5rem .1rem", lineHeight: 1.5, maxWidth: 340 }}>
              "{sentence}"
            </div>
          )}
          <input ref={inputRef} className={`ans-input ${done ? (correct ? "ok" : "bad") : ""}`}
            type="text" placeholder="Type the word here…" value={ans}
            onChange={e => setAns(e.target.value)}
            onKeyDown={e => e.key === "Enter" && !done && submit()}
            disabled={done} autoComplete="off" autoCorrect="off" autoCapitalize="none" spellCheck={false} />
          {!done && <button className="submit-btn" onClick={submit} disabled={!ans.trim()}>Submit Answer</button>}
        </div>
        {done && (
          <>
            <div className={`fb-banner ${correct ? "ok" : "bad"}`}>
              {correct ? "✅ Correct! Great job." : <span>❌ Correct spelling: <strong>{word.word}</strong></span>}
            </div>
            <div style={{ display: "flex", gap: ".75rem", alignItems: "center", justifyContent: "center" }}>
              <button className="next-btn" onClick={next}>{idx + 1 >= words.length ? "See Results →" : "Next Word →"}</button>
              <button className="next-btn" style={{ background: "var(--purple)", display: "flex", alignItems: "center", gap: ".4rem" }}
                onClick={() => setDictWord(word.word)}>
                📖 Definition
              </button>
            </div>
          </>
        )}
        <div style={{ display: "flex", gap: ".5rem", justifyContent: "center", marginTop: "1.25rem" }}>
          <button style={{ background: "var(--amber)", color: "#fff", border: "none", borderRadius: 20, padding: ".3rem .8rem", fontSize: ".73rem", fontWeight: 700, cursor: "pointer" }}
            onClick={handleSaveAndExit}>💾 Save & Exit</button>
          <button style={{ background: "transparent", color: "var(--muted)", border: "1px solid var(--border)", borderRadius: 20, padding: ".3rem .8rem", fontSize: ".73rem", fontWeight: 600, cursor: "pointer" }}
            onClick={() => onExit?.()}>✕ Exit</button>
        </div>
      </div>
    </div>
  );
}
